import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { showError, showMessage } from '../../actions/uiActions';
import {
    Button,
    Switch,
    Accordion,
    AccordionSummary,
    Typography,
    AccordionDetails,
    IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import {
    sortableContainer,
    sortableElement,
    sortableHandle,
} from 'react-sortable-hoc';
import { arrayMoveImmutable as arrayMove } from 'array-move';

import ProductConfiguration from './ProductConfiguration';
import DomainApi from '../../api/domains';
import { urlRegex } from '../../constant';
import CustomProduct from './CustomProduct';

const PRODUCTS = [
    { name: 'pslf', title: 'Public Service Loan Forgiveness', description: 'Receive assistance with the Public Service Loan Forgiveness program' },
    { name: 'trs', title: 'Tuition Reimbursement Services', description: 'Manage my course requests and tuition reimbursement' },
    { name: 'slra', title: 'Student Loan Repayment Assistance', description: "Manage my employer's student loan contribution" },
    { name: 'cfp', title: 'College Financial Planning (CFP)', description: 'Receive College Financial Planning assistance' },
    { name: 'peat', title: 'PeopleJoy Education Access Tool', description: 'Access exclusive discounts on career-boosting programs through our Education Access Tool' },
];

const DragHandle = sortableHandle(() => (
    <IconButton
        size='small'
        aria-label='drag-handle'
        sx={{ cursor: 'grab', padding: 0, marginRight: 1 }}
    >
        <DragIndicatorIcon sx={{ color: 'black' }} />
    </IconButton>
));

const SortableItem = sortableElement(
    ({ product, productConfig, onToggle, handleChangeProductConfig, errors }) => {
        return (
            <div className='rounded bg-light shadow-sm w-100 mb-3 p-3 dragging-item' key={product.name}>
                <div className='d-flex align-items-center justify-content-between'>
                    <div className='d-flex align-items-center'>
                        {productConfig && <DragHandle />}
                        <span className='fw-bold'>{product.title}</span>
                    </div>

                    <Switch
                        size='medium'
                        color='primary'
                        checked={!!productConfig}
                        onChange={(e) => onToggle(e, product.name)}
                    />
                </div>

                {productConfig && (
                    <Accordion defaultExpanded={false}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`${product.name}-content`}
                            id={`${product.name}-header`}
                        >
                            <Typography variant='subtitle1'>
                                Configure {product.title}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <ProductConfiguration
                                product={productConfig}
                                onChange={handleChangeProductConfig}
                                errors={errors[product.name] || {}}
                            />
                        </AccordionDetails>
                    </Accordion>
                )}
            </div>
        );
    }
);

const SortableList = sortableContainer(
    ({ enabledProducts, selectedProducts, onToggle, handleChangeProductConfig, errors }) => {
        return (
            <div className='ml-3 mt-4 sortable-wrapper'>
                {enabledProducts.map((product, index) => {
                    const productConfig = selectedProducts.find((p) => p.name === product.name);
                    return (
                        <SortableItem
                            key={`item-${product.name}`}
                            index={index}
                            product={product}
                            productConfig={productConfig}
                            onToggle={onToggle}
                            handleChangeProductConfig={handleChangeProductConfig}
                            errors={errors}
                        />
                    );
                })}
            </div>
        );
    },
    {
        helperClass: 'dragging-item',
        helperContainer: () => document.querySelector('.MuiDialog-paper') || document.body,
    }
);

const SplashPageConfigContent = ({ domain, onSaveSuccess, showError, showMessage }) => {
    const [orderedProducts, setOrderedProducts] = useState(() => [...PRODUCTS]);
    const [selectedProducts, setSelectedProducts] = useState(domain?.splashPageConfig?.products || []);
    const [addCustomProduct, setAddCustomProduct] = useState(false);
    const [errors, setErrors] = useState({});

    const domainLink = useMemo(() => `https://app.peoplejoy.com/${domain?.domain}/welcome`, [domain]);

    useEffect(() => {
        const domainProducts = domain?.splashPageConfig?.products || [];
        const domainProductNames = new Set(domainProducts.map((p) => p.name));
        const newOrdered = [...domainProducts, ...PRODUCTS.filter((p) => !domainProductNames.has(p.name))];
        setOrderedProducts(newOrdered);
        setSelectedProducts(domainProducts);
    }, [domain]);

    const getGroupedProducts = (products, selected) => {
        const selectedNames = new Set(selected.map((p) => p.name));
        const enabled = products.filter((p) => selectedNames.has(p.name));
        const disabled = products.filter((p) => !selectedNames.has(p.name));
        return [...enabled, ...disabled];
    };

    const syncSelectedWithOrder = (newOrdered) => {
        setSelectedProducts((prevSelected) => {
            const selectedNames = new Set(prevSelected.map((p) => p.name));
            return newOrdered
                .filter((p) => selectedNames.has(p.name))
                .map((p) => {
                    const existing = prevSelected.find((sp) => sp.name === p.name);
                    return (
                        existing || {
                            ...p,
                            clickAction: 'direct_link',
                            infoBannerEnabled: false,
                        }
                    );
                });
        });
    };

    const handleAddProduct = (e, name) => {
        if (e.target.checked) {
            const productFromOrdered = orderedProducts.find((item) => item.name === name) || PRODUCTS.find((item) => item.name === name);
            const newSelected = [
                ...selectedProducts,
                {
                    ...productFromOrdered,
                    clickAction: 'direct_link',
                    infoBannerEnabled: false,
                },
            ];
            setSelectedProducts(newSelected);
            setOrderedProducts((prev) => getGroupedProducts(prev, newSelected));
        } else {
            const newSelected = selectedProducts.filter((item) => item.name !== name);
            setSelectedProducts(newSelected);
            setErrors((prev) => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });

            setOrderedProducts((prev) => getGroupedProducts(prev, newSelected));
        }
    };

    const handleChangeProductConfig = (name, updatedFields) => {
        setSelectedProducts((prev) => prev.map((item) => (item.name === name ? { ...item, ...updatedFields } : item)));
        setOrderedProducts((prev) => prev.map((item) => (item.name === name ? { ...item, ...updatedFields } : item)));
        setErrors((prev) => {
            const currentErrors = prev[name] || {};
            const clearedErrors = { ...currentErrors };
            Object.keys(updatedFields).forEach((field) => {
                clearedErrors[field] = '';
            });
            return { ...prev, [name]: clearedErrors };
        });
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const selectedNames = new Set(selectedProducts.map((p) => p.name));
        const enabled = orderedProducts.filter((p) => selectedNames.has(p.name));
        const disabled = orderedProducts.filter((p) => !selectedNames.has(p.name));

        const newEnabled = arrayMove(enabled, oldIndex, newIndex);

        const newOrdered = [...newEnabled, ...disabled];
        setOrderedProducts(newOrdered);
        syncSelectedWithOrder(newOrdered);
    };

    const validateProducts = () => {
        const newErrors = {};
        selectedProducts.forEach((product) => {
            const { name, title, description, clickAction, directLinkUrl, steps, infoBannerEnabled } = product;
            const pErrors = {};
            if (!title?.trim()) pErrors.title = 'Title is required';
            if (title.trim().length > 80) pErrors.title = 'Title cannot exceed 80 characters';
            if (description && description.trim().length > 300) pErrors.description = 'Description cannot exceed 300 characters';
            if (!clickAction?.trim()) pErrors.clickAction = 'Click action is required';
            if (infoBannerEnabled && !product.infoBannerText?.trim()) pErrors.infoBannerText = 'Info banner text is required';
            if (clickAction === 'direct_link') {
                if (!directLinkUrl?.trim()) {
                    pErrors.directLinkUrl = 'Direct link URL is required';
                } else if (!urlRegex.test(directLinkUrl.trim())) {
                    pErrors.directLinkUrl = 'Please enter a valid URL (e.g. example.com/page)';
                }
            }
            if (clickAction === 'open_modal') {
                if (!Array.isArray(steps) || steps.length === 0) {
                    pErrors.steps = 'At least one step is required';
                }
            }
            if (Object.keys(pErrors).length) newErrors[name] = pErrors;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveProductConfig = async () => {
        try {
            if (selectedProducts.length > 0 && !validateProducts()) {
                showError('Please fix the errors in the configuration.');
                return;
            }
            await DomainApi.updateSplashPage(domain._id, {
                ...domain.splashPageConfig, products: selectedProducts
            });
            showMessage('Splash page configuration saved successfully.');
            onSaveSuccess(domain._id, { ...domain.splashPageConfig, products: selectedProducts });
        } catch (error) {
            showError(error.message || 'An error occurred while saving configuration.');
        }
    };

    const selectedNamesSet = new Set(selectedProducts.map((p) => p.name));
    const enabledOrdered = useMemo(() => orderedProducts.filter((p) => selectedNamesSet.has(p.name)), [orderedProducts, selectedProducts]);
    const disabledOrdered = orderedProducts.filter((p) => !selectedNamesSet.has(p.name));

    const handleAddCustomProduct = (product) => {
        const newlyAddedProduct = { name: `custom_product_${Date.now()}`, clickAction: 'direct_link', ...product };
        const newSelected = [...selectedProducts, newlyAddedProduct];
        setSelectedProducts(newSelected);
        const newOrdered = [newlyAddedProduct, ...orderedProducts];
        setOrderedProducts(newOrdered);
    };

    return (
        <div>
            <div className='flex align-items-center justify-content-between mb-3'>
                <Typography variant="body2" component="div">
                    Customize the domain's splash page users will see when they visit
                    <a href={domainLink} target='_blank' rel='noreferrer'> {domainLink}</a>
                </Typography>
                <Button variant='text' onClick={() => setAddCustomProduct(true)}>Add Custom Product</Button>
            </div>

            <SortableList
                enabledProducts={enabledOrdered}
                selectedProducts={selectedProducts}
                onToggle={handleAddProduct}
                handleChangeProductConfig={handleChangeProductConfig}
                errors={errors}
                onSortEnd={onSortEnd}
                useDragHandle
                helperClass='dragging-item'
            />

            <div className='ml-3 mt-4'>
                {disabledOrdered.map((product) => {
                    const productConfig = selectedProducts.find((p) => p.name === product.name);
                    return (
                        <div className='rounded bg-light shadow-sm w-100 mb-3 p-3' key={product.name}>
                            <div className='d-flex align-items-center justify-content-between'>
                                <span className='fw-bold'>{product.title}</span>
                                <Switch
                                    size='medium'
                                    color='primary'
                                    checked={!!productConfig}
                                    onChange={(e) => handleAddProduct(e, product.name)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="d-flex justify-content-end mt-4">
                <Button variant="outlined" onClick={() => window.history.back()}>Cancel</Button>
                <Button variant="contained" onClick={handleSaveProductConfig} sx={{ ml: 2 }}>Save</Button>
            </div>

            {addCustomProduct && (
                <CustomProduct
                    close={() => setAddCustomProduct(false)}
                    onAddProduct={handleAddCustomProduct}
                />
            )}
        </div>
    );
};

export default connect(null, { showError, showMessage })(SplashPageConfigContent);
